// FudiTap guest menu contract v1 -> Customer PWA menu model.
// Contract: nfc-panel docs/architecture/FUDITAP_GUEST_MENU_CONTRACT.md
//
// The response is validated field by field and rebuilt; unknown fields are
// ignored, never copied. Anything malformed fails the whole menu closed — the
// PWA then shows an error state, never another restaurant's (mock) menu.

export const GUEST_MENU_VERSION = 1;

export class GuestMenuError extends Error {
  // reason: "not_found" | "unavailable" | "network" | "malformed"
  constructor(reason) {
    super(reason);
    this.name = "GuestMenuError";
    this.reason = reason;
  }
}

// Same rule as the backend and the /c/[code] resolver: 6 chars A-Z0-9 after trim + upper case.
export function normalizeCardCode(raw) {
  if (typeof raw !== "string") return null;
  const code = raw.trim().toUpperCase();
  return /^[A-Z0-9]{6}$/.test(code) ? code : null;
}

const chars = (v) => [...v].length;
const isId = (v) => typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v);
const isName = (v) => {
  if (typeof v !== "string") return false;
  const n = chars(v.replace(/^ +| +$/g, ""));
  return n >= 1 && n <= 200;
};
const isLabel = (v) => typeof v === "string" && chars(v) <= 200;
const isInt = (v, min, max) => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;
const isImage = (v) => v === null || (typeof v === "string" && chars(v) <= 2048 && /^https:\/\/[^\s]+$/.test(v));
const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

// Step 2 additive fields (table label, EU-14 allergens, nutrition, option groups).
export const ALLERGENS = [
  "gluten", "crustaceans", "eggs", "fish", "peanuts", "soybeans", "milk", "nuts",
  "celery", "mustard", "sesame", "sulphites", "lupin", "molluscs",
];
const NUTRITION_KEYS = ["energy_kcal", "protein_g", "carbs_g", "sugar_g", "fat_g", "saturated_fat_g", "fiber_g", "salt_g"];
// Backend allergen id -> existing PWA allergenMeta id (others are shown by id).
const PWA_ALLERGEN = { milk: "dairy", eggs: "egg" };

// High-entropy table QR token (32 hex); separate namespace from card codes.
export function normalizeTableToken(raw) {
  if (typeof raw !== "string") return null;
  const token = raw.trim().toLowerCase();
  return /^[0-9a-f]{32}$/.test(token) ? token : null;
}

/** Validate a v1 response and return a clean copy with only contract fields. */
export function parseGuestMenuV1(json) {
  const bad = () => { throw new GuestMenuError("malformed"); };
  if (!isObject(json) || json.version !== GUEST_MENU_VERSION || json.currency !== "TRY" ||
      !isObject(json.business) || !isObject(json.branch) || !Array.isArray(json.categories)) bad();
  if (!isLabel(json.business.name) || !isLabel(json.branch.name)) bad();
  let table = null;
  if (json.table !== null && json.table !== undefined) {
    if (!isObject(json.table) || typeof json.table.label !== "string" ||
        chars(json.table.label) < 1 || chars(json.table.label) > 40) bad();
    table = { label: json.table.label };
  }
  const seen = new Set();
  const unique = (id) => { if (!isId(id) || seen.has(id)) return false; seen.add(id); return true; };
  const allergens = (raw) => {
    if (raw === undefined) return [];
    if (!Array.isArray(raw) || raw.length > 14 || new Set(raw).size !== raw.length ||
        !raw.every((a) => ALLERGENS.includes(a))) bad();
    return [...raw];
  };
  const nutrition = (raw) => {
    if (raw === undefined || raw === null) return null;
    if (!isObject(raw)) bad();
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      if (!NUTRITION_KEYS.includes(k) || typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 100000) bad();
      out[k] = v;
    }
    return out;
  };
  const optionGroups = (raw) => {
    if (raw === undefined) return [];
    if (!Array.isArray(raw) || raw.length > 50) bad();
    return raw.map((g) => {
      if (!isObject(g) || !unique(g.id) || !isName(g.name) || !isInt(g.minSelect, 0, 20) ||
          !isInt(g.maxSelect, 1, 20) || g.maxSelect < g.minSelect || !Array.isArray(g.options)) bad();
      const options = g.options.map((o) => {
        if (!isObject(o) || !unique(o.id) || !isName(o.name) || !isInt(o.priceDeltaMinor, -1000000, 1000000)) bad();
        return { id: o.id, name: o.name, priceDeltaMinor: o.priceDeltaMinor };
      });
      return { id: g.id, name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, options };
    });
  };
  const categories = json.categories.map((c) => {
    if (!isObject(c) || !unique(c.id) || !isName(c.name) ||
        !isInt(c.sortOrder, -2147483648, 2147483647) || !Array.isArray(c.products)) bad();
    const products = c.products.map((p) => {
      if (!isObject(p) || !unique(p.id) || !isName(p.name) ||
          typeof p.description !== "string" || chars(p.description) > 4000 ||
          !isInt(p.priceMinor, 0, 2147483647) || !isImage(p.image)) bad();
      return { id: p.id, name: p.name, description: p.description, priceMinor: p.priceMinor, image: p.image,
        allergens: allergens(p.allergens), nutrition: nutrition(p.nutrition), optionGroups: optionGroups(p.optionGroups) };
    });
    return { id: c.id, name: c.name, sortOrder: c.sortOrder, products };
  });
  return { version: GUEST_MENU_VERSION, business: { name: json.business.name },
    branch: { name: json.branch.name }, table, currency: "TRY", categories };
}

/**
 * Map a parsed v1 menu to the existing PWA domain model (src/data/menu.js shape).
 * Fields the backend does not provide get neutral values: never invented
 * badges, nutrition, allergens, prep times or modifiers. Nutrition is shown
 * only when the business supplied all four PWA keys (kcal/protein/carbs/fat).
 */
function toPwaNutrition(n) {
  if (!n || ![n.energy_kcal, n.protein_g, n.carbs_g, n.fat_g].every((v) => typeof v === "number")) return null;
  return { calories: n.energy_kcal, protein: n.protein_g, carbs: n.carbs_g, fat: n.fat_g };
}

export function toPwaMenu(menu) {
  const categories = menu.categories.map((c) => ({
    id: c.id, slug: `cat-${c.id}`, icon: null, name: c.name, sortOrder: c.sortOrder,
  }));
  const products = menu.categories.flatMap((c) => c.products.map((p) => ({
    id: p.id,
    categoryId: c.id,
    name: p.name,
    description: p.description,
    priceMinor: p.priceMinor,
    basePrice: p.priceMinor / 100, // display only (kuruş -> TRY); totals belong to the backend
    image: p.image,
    available: true, // the API only returns available products
    popular: false, spicy: false, vegetarian: false,
    prepTimeMin: null, ingredients: null,
    allergens: [...new Set((p.allergens || []).map((a) => PWA_ALLERGEN[a] || a))],
    nutrition: toPwaNutrition(p.nutrition),
    // Option prices are display only; the server recomputes every line.
    modifierGroups: (p.optionGroups || []).map((g) => ({
      id: g.id, name: g.name, required: g.minSelect > 0, min: g.minSelect, max: g.maxSelect,
      options: g.options.map((o) => ({ id: o.id, name: o.name, priceDelta: o.priceDeltaMinor / 100, priceDeltaMinor: o.priceDeltaMinor })),
    })),
  })));
  return {
    restaurant: { name: menu.business.name, branchName: menu.branch.name, currency: menu.currency,
      tableLabel: menu.table ? menu.table.label : null },
    categories,
    products,
  };
}

/**
 * Fetch, validate and map the guest menu for one entry: a card code (string or
 * { cardCode }) or a table QR token ({ tableToken }). Exactly one key is sent.
 * Throws GuestMenuError; the caller must render an error/empty state.
 */
export async function loadGuestMenu(rawEntry, { baseUrl = "", fetchImpl = globalThis.fetch, timeoutMs = 10000 } = {}) {
  const code = typeof rawEntry === "string" ? normalizeCardCode(rawEntry)
    : rawEntry && rawEntry.tableToken === undefined ? normalizeCardCode(rawEntry.cardCode) : null;
  const table = rawEntry && typeof rawEntry === "object" && rawEntry.cardCode === undefined ? normalizeTableToken(rawEntry.tableToken) : null;
  if (!code && !table) throw new GuestMenuError("not_found");
  const query = code ? `card=${encodeURIComponent(code)}` : `table=${encodeURIComponent(table)}`;
  if (typeof fetchImpl !== "function") throw new GuestMenuError("network");
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let response;
  try {
    response = await fetchImpl(`${baseUrl}/api/guest/menu?${query}`, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      headers: { Accept: "application/json" },
      signal: controller?.signal,
    });
  } catch {
    throw new GuestMenuError("network");
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (response.status === 404 || response.status === 403) throw new GuestMenuError("not_found");
  if (!response.ok) throw new GuestMenuError("unavailable");
  let json;
  try {
    json = await response.json();
  } catch {
    throw new GuestMenuError("malformed");
  }
  return toPwaMenu(parseGuestMenuV1(json));
}
